import React, {useState} from "react"
import {useDarkMode} from "../../hooks/useDarkMode"
import CommandHistoryPanel from "../CommandHistoryPanel"
import {useAuthStore, useIsDeveloper, useUserRole} from "../../store/authStore"

interface DashboardHeaderProps {
	isLiveMode: boolean
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({isLiveMode}) => {
	const {isDark, toggle} = useDarkMode()
	const [historyOpen, setHistoryOpen] = useState(false)
	const isDeveloper = useIsDeveloper()
	const role = useUserRole()
	const isViewer = role === "viewer"
	const isViewerAll = role === "viewer_all"
	const showEmail = isDeveloper || isViewer || isViewerAll
	const email = useAuthStore((state) => state.userProfile?.email)

	return (
		<>
			<div className="flex items-center gap-3 mb-4">
				<img src="/app_logo_1024_14.png" alt="AiWave Logo" className="w-10 h-10 flex-shrink-0" />
				<h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3 flex-1">
					AiWave IoT Device Dashboard
					<span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300">
						ACC
					</span>
					{isDeveloper && (
						<span
							title="Developer access: only your own devices and devices in your own or shared locations"
							className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-300">
							DEVELOPER
						</span>
					)}
					{(isViewer || isViewerAll) && (
						<span
							title={
								isViewerAll
									? "Read-only access: all devices, no changes allowed"
									: "Read-only access: only your own devices and devices in your own or shared locations, no changes allowed"
							}
							className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-slate-200 dark:bg-slate-700/60 text-slate-800 dark:text-slate-200">
							{isViewerAll ? "VIEWER ALL" : "VIEWER"}
						</span>
					)}
					{isLiveMode && (
						<span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">
							<span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse" />
							Live Mode
						</span>
					)}
				</h1>

				{showEmail && email && (
					<span className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-xs" title={email}>
						{email}
					</span>
				)}

				<button
					onClick={() => setHistoryOpen(true)}
					aria-label="Open command history"
					className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0">
					<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
						/>
					</svg>
					History
				</button>

				<button
					onClick={toggle}
					aria-label="Toggle dark mode"
					className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0">
					{isDark ? (
						<>
							<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 7a5 5 0 100 10A5 5 0 0012 7z"
								/>
							</svg>
							Light
						</>
					) : (
						<>
							<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
								/>
							</svg>
							Dark
						</>
					)}
				</button>
			</div>

			<CommandHistoryPanel isOpen={historyOpen} onClose={() => setHistoryOpen(false)} />
		</>
	)
}

export default DashboardHeader
